import {
	domReady,
	initBlockEnterKeyForDisabledButton,
	setButtonStateDuringActivity,
	initOnIntersection,
	lazyPageFeatureRequests,
	waitForElementText
} from 'utilities/dom';
import { formatCurrency } from 'utilities/localization';
import { declareJavaScriptInitialized, getQueryStringValue } from 'utilities/browser';
import { openMessenger } from 'features/genesysCloud';
import { Reporter, visibilityObserver } from 'features/dataCapture';
import lpSelect from 'ui/select/lpSelect';
import unveil from 'ui/lazy-load/unveil';
import relatedVideos from 'features/relatedVideos/desktop';
import { utagLinkChat, trackReplacementPartsActions, utagLinkPDP } from 'features/tealium';
import { recentlyViewedGAEvents } from 'features/recently-viewed';
import { interfaceTypes } from 'features/productRelationships/constants';
import { prefetchWebpackAssetSet } from 'features/webpack';
import { deleteReferringSortSessionData, getReferringSortSessionData } from 'features/sort';
import { viewItem } from 'features/tealium/ga4/ecommerce';
import { setGenericEvent, setVideoLoad } from 'features/tealium/ga4/custom';
import { renderPayPalMessage, getPayPalLaterPaymentPlan } from 'features/paypal';
import initTabHandling from 'features/product/initTabHandling';
// extending coupon modal to PDP without changing source code location (currently in sort)
// eslint-disable-next-line no-restricted-imports
import initCouponModal from 'pages/sort/desktop/couponModal';
import { trackDyRecommendedItemClickEvent } from 'features/tealium/dynamicYield/recommendedItemsTracking';
import { getProductDeliveryDate, getProductDeliveryDates } from 'features/atp/api';
import { getLatestDeliveryInfoForKitSkus } from 'features/atp/utilities';
import { renderPromoCountdown } from 'features/promo';
import { FALLBACK_CALLOUT_TEXT, FALLBACK_VARIATIONS } from 'features/atp/config';

import dynamicImport from '../../../features/webpack/dynamicImport.macro';

import initProductRelationships from './productRelationships';
import scrollToSection from './scrollToSection';
import initCart from './cart';
import initCommon from './common';
import initGiftCard from './giftCard';
import initImage from './image';
import initShareProduct from './shareProduct';
import initStickyHeader from './stickyHeader';
import initEmailSubscribe from './emailSubscribe';
import initWishList from './wishList';
import initLightingCollectionsSlideOut from './lightingCollectionsSlideOut';
import initFreeShippingTooltip from './freeShippingTooltip';
import {
	getMultipleDeliveryInfoAndRender,
	initAtpDeliveryInfo,
	initAtpDeliveryInfoForKitSkus,
	initMultipleAtpDeliveryInfo,
	renderAtpDeliveryInfo
} from './atp';

const trackEventChat = utagLinkChat();

domReady(() => {

	// bind GA events when Recently Viewed Widget is ready
	document.addEventListener('recommendedItems:loaded', () => recentlyViewedGAEvents(utagLinkPDP()));

	const {
		recommendedBulbTypeCount,
		showReplacementParts,
		showRecommendedBulbs,
		employeeNumber,
		inStockLabelDays,
		isOpenBox,
		isGiftCard,
		productAvailability,
		showPaypalLaterWidget,
		ugcApiAvailable,
		ugcProductData: productData,
		ugcQuestionsData: questionsData,
		ugcReviewsData: reviewsData,
		ugcReviewsSummaryData: reviewsSummaryData,
		relationshipGroupsData,
		shippingTerms,
		lightingCollectionName,
		frequentlyBoughtTogetherData,
		couponCode,
		isCouponCodeValid,
		googleMapsApiKey,
		googleMapsApiVersion,
		isAtpToggleOn,
		kitSkuComponents,
		atpShippingFallbackLabel,
		atpDeliveryFallbackLabel,
		showShipsToday,
		shipsTodayCutoffTime,
		shipsTodayIsBeforeCutoff
	} = window.lp.getPageData();

	const {
		sortPath, sortPosition, sortSku, listType, searchSkuCount
	} = getReferringSortSessionData();

	deleteReferringSortSessionData();

	// Page state
	const pageState = {
		qaInitialized: false,
		reviewsInitialized: false,
		multiProductSkuChanged: false,
		productData,
		questionsData,
		reviewsData,
		reviewsSummaryData
	};

	// on page load only render first 5 reviews
	if (pageState.reviewsData?.results?.length > 5) {

		pageState.reviewsData.results = pageState.reviewsData.results.splice(0, 5);

	}

	if (showReplacementParts || showRecommendedBulbs) {

		const pdReplacementPartModal = document.querySelector('#pdReplacementPartModal');

		if (pdReplacementPartModal) {

			const shortSku = pdReplacementPartModal.dataset.shortsku;

			if (showReplacementParts) {

				trackReplacementPartsActions({
					eventAction: 'Load-Parts',
					eventLabel: shortSku
				});

			}

			if (showRecommendedBulbs) {

				utagLinkPDP()({
					eventAction: 'Load-Recommended-Bulb',
					eventLabel: window.location.pathname,
					nonInteraction: 1
				});

			}
			setGenericEvent({
				event_name: 'pdp_parts_load',
				pdp_item_id: window.shortSku,
				pdp_prime_cat: window.productCategory
			});

			pdReplacementPartModal.addEventListener('click', (e) => {

				e.preventDefault();
				dynamicImport('desktop/product-replacementPartModal', './replacementPartModal').then((module) => {

					const initReplacementPart = module.default;
					initReplacementPart(shortSku, showReplacementParts, showRecommendedBulbs);

				});

			});

		}

	}
	const lightingCollectionsSlideOutLink = document.querySelector('#lightingCollectionsSlideOutContainer');

	if (lightingCollectionsSlideOutLink) {

		initLightingCollectionsSlideOut(window.shortSku, lightingCollectionName);

	}

	const frequentlyBoughtTogetherElement = document.querySelector('#frequentlyBoughtTogetherPDP');

	if (frequentlyBoughtTogetherElement) {

		dynamicImport('desktop/product-frequentlyBoughtTogether', './frequentlyBoughtTogether').then((module) => {

			const initFrequentlyBoughtTogether = module.default;
			initFrequentlyBoughtTogether(
				frequentlyBoughtTogetherData.Products,
				window.shortSku,
				frequentlyBoughtTogetherElement
			);

		});

	}

	if (window.lp.globals.isChatToggleOn) {

		const chatButtons = document.querySelectorAll(
			'[data-ga-label="PDP-ProdToolBar"], [data-ga-label="PDP-QA-ChatContainer"]'
		);

		chatButtons.forEach((chat) => {

			chat.addEventListener('click', (button) => {

				openMessenger();
				const eventLabel = button.currentTarget.dataset.gaLabel;
				trackEventChat({
					eventAction: 'Request-Chat',
					eventLabel
				});
				setGenericEvent({
					event_name: 'chat_click',
					chat_button_type: 'static',
					chat_button_text: button.target.innerHTML
				});

			});

		});

		const label = [ 'PDP-ProdToolBar' ];
		const turntoQuestionsAndAnswersSection = document.querySelector('#turntoQuestionsAndAnswersSection');
		if (turntoQuestionsAndAnswersSection) label.push('PDP-QA-ChatContainer');
		label.forEach((eventLabel) => {

			trackEventChat({
				eventAction: 'Load-Chat',
				eventLabel,
				nonInteraction: 1
			});

		});
		const liveChat = document.querySelector('#pnlHousingOptions');
		if (liveChat) {

			trackEventChat({
				eventAction: 'Load-Chat',
				eventLabel: 'PDP-Recessed-Housing',
				nonInteraction: 1
			});

			const chatButton = liveChat.querySelector('.chatWrapperWithIcon button');
			chatButton.addEventListener('click', (button) => {

				openMessenger();
				const eventLabel = button.currentTarget.dataset.gaLabel;
				trackEventChat({
					eventAction: 'Request-Chat',
					eventLabel
				});
				setGenericEvent({
					event_name: 'chat_click',
					chat_button_type: 'static',
					chat_button_text: button.target.innerHTML
				});

			});

		}

		const PDPRepChat = document.querySelector('#pdProdInfoCol .needHelp .chatWrapperWithIcon button');
		if (PDPRepChat) {

			PDPRepChat.addEventListener('click', (e) => {

				openMessenger();
				trackEventChat({
					eventAction: 'Request-Chat',
					eventLabel: ''
				});
				setGenericEvent({
					event_name: 'chat_click',
					chat_button_type: 'static',
					chat_button_text: e.target.innerHTML
				});

			});

		}

		const PDPUnavailableProductChatBtn = document.querySelector(
			'#pdProdInfoCol .pdPleaseCall .chatWrapperWithIcon button'
		);

		if (PDPUnavailableProductChatBtn) {

			PDPUnavailableProductChatBtn.addEventListener('click', (e) => {

				openMessenger();
				trackEventChat({
					eventAction: 'Request-Chat',
					eventLabel: ''
				});
				setGenericEvent({
					event_name: 'chat_click',
					chat_button_type: 'static',
					chat_button_text: e.target.innerHTML
				});

			});

		}

	}

	// initialize product relationships on page load
	initProductRelationships(interfaceTypes.desktop, relationshipGroupsData);
	// Below: migrated from Scripts/product/page.js

	lp.pdp = lp.pdp || {};

	const { enableDataCapture, isUserBopusEligible, isLoggedIn } = window.lp.globals;
	const trackProductPageEvents = utagLinkPDP();

	// PDP app
	const initialShortSku = window.shortSku;

	lp.pdp.page = () => {

		if (productAvailability?.callForAvailability && !isLoggedIn) {

			$('body').lpModal({
				onInit: true,
				fullScreen: false,
				appendContentToForm: false,
				content: $('#callForAvailabillityContainer'),
				showCloseBtn: true,
				lpModalClass: 'callForAvailabillityModal hidden',
				height: 90,
				width: 290,
				backdrop: false,
				openEvt: () => {

					const lpModalClose = document.getElementById('lpModalClose');
					if (lpModalClose && lpModalClose.innerHTML) {

						lpModalClose.innerHTML = '<span class="lpIcon-close02"></span>';

					}

					initEmailSubscribe();

				},
				closeEvt: () => { }
			});

		}

		function initInputFilters () {

			// Input filters
			$('.systemOptionsQty, .pdHousingOptionsQTY').inputFilter({ type: 'integer' });

			// Fields
			$('#QtyNormal')
				// Input filter for Quantity field
				.inputFilter({ type: 'integer' })
				// add product to cart with Enter on Normal Product Pages

				.keypress((e) => {

					// Add to cart if button is visible (not shown if Sold Out)
					if (e.which === 13 && $('#pdAddToCart:visible').length > 0) {

						$('#pdAddToCart').click();
						e.preventDefault();

					}

				});

			$('#QtyMultiProd')
				// Input filter for Quantity field
				.inputFilter({ type: 'integer' })
				// add product to cart with Enter on Multiple Choice Product Pages
				.keypress((e) => {

					// Add to cart if button is visible (not shown if Sold Out)
					if (e.which === 13 && $('#AddToCart_Multiproduct:visible').length > 0) {

						$('#AddToCart_Multiproduct').click();
						e.preventDefault();

					}

				});

			// add product to cart with Enter on "With Face Plate" quantity
			$('#QtyDimmerCombo')
				// Input filter for Quantity field
				.inputFilter({ type: 'integer' })
				// add product to cart with Enter
				.keypress((e) => {

					// Add to cart if button is visible (not shown if Sold Out)
					if (e.which === 13 && $('#CartAddDimmer:visible').length > 0) {

						$('#CartAddDimmer').click();
						e.preventDefault();

					}

				});

		}

		function initializeBopus () {

			// Clicking Change Store link in Store Pickup option opens Store Locations submenu
			$('#bopusOptions .changeStore')
				.off('click.bopus')
				.on('click.bopus', function (e) {

					const shortSku = $(this).attr('data-shortsku');
					const productName = $(this).data('productName');
					const imageUrl = $(this).data('imageUrl');
					const storeSelected = $('#storePickupRadio').val();

					e.preventDefault();

					const changePickupOptionsView = new window.lp.bopus.ChangePickupOptionsView({
						model: new Backbone.Model({
							Name: productName,
							ProductImageUrl: imageUrl,
							ShortSku: shortSku,
							IsBopusEligible: isUserBopusEligible,
							PickUpStoreNumber: Number(storeSelected)
						}),
						comboSkuItem: this.comboSkuItem
					});
					changePickupOptionsView.render();

				});

		}

		function initLpSelect () {

			lpSelect(document.querySelector('.lpSelect'));

		}

		function intializeProductAssets () {

			const pdProductAssetsSection = document.getElementById('pdProductAssetsSection');
			const pdProductAssetsLinksList = document.getElementById('pdProductAssetLinksList');
			const productName = document.getElementById('h1ProductName').textContent;
			// reference sticky wrapper element for hiding it when product asset document is opened
			const stickyWrapper = document.getElementById('stickyWrapper-sticky-wrapper');

			fetch(`/api/productdetail/${window.shortSku}/`, {
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json; charset=utf-8'
				},
				credentials: 'same-origin',
				method: 'GET'
			})
				.then((res) => res.json())
				.then((productAssets) => productAssets.map((asset) => {

					const li = document.createElement('li');
					const a = document.createElement('a');
					a.href = '#';
					a.id = `${asset.Name.split(' ').join('')}_${asset.Id}`;
					a.classList.add('productAssetLink');
					a.dataset.filepath = asset.FilePath;
					a.innerHTML = `${asset.Name} for ${productName}`;
					li.appendChild(a);
					pdProductAssetsLinksList.appendChild(li);
					trackProductPageEvents({
						eventAction: 'Load-Product-Documents',
						eventLabel: window.shortSku,
						nonInteraction: 1
					});

					a.addEventListener('click', (e) => {

						e.preventDefault();
						trackProductPageEvents({
							eventAction: 'Click-Product-Documents',
							eventLabel: `${window.shortSku}-${asset.FilePath}`,
							nonInteraction: 0
						});

						$('body').lpModal({
							onInit: true,
							fullScreen: true,
							src: e.target.dataset.filepath,
							showCloseBtn: true,
							lpModalClass: 'productAssetsPreviewModal',
							openEvt: () => {

								if (stickyWrapper) {

									stickyWrapper.style.display = 'none';

								}

							},
							closeEvt: () => {

								if (stickyWrapper) {

									stickyWrapper.style.display = '';

								}

							}
						});

					});

					return asset;

				}))
				.then((res) => res.length > 0 && pdProductAssetsSection.classList.remove('hidden'))
				.catch((error) => console.log(error));

		}

		function widgetPopularTrackingHandler () {

			const widgetSelector = '#workStationBaseAccordion';
			const widget = document.querySelector(widgetSelector);

			if (widget) {

				trackProductPageEvents({
					eventAction: 'Load-Popular-Accessories',
					nonInteraction: 1
				});

				widget.querySelectorAll('.scrollable .pdWorkStationBasesThumb').forEach((item) => {

					item.addEventListener('click', (e) => {

						const pdWorkStationBasesThumb = e.currentTarget;
						const targetedElement = e.target;
						const inputCheck = pdWorkStationBasesThumb.querySelector('input[type=checkbox]');
						const sku = pdWorkStationBasesThumb.dataset.id;
						if (
							sku
							&& inputCheck.checked
							&& (targetedElement.classList.contains('workStationBasesCheckWrapper')
								|| targetedElement.classList.contains('js-widget-rdo'))
						) {

							trackProductPageEvents({
								eventAction: 'Select-Popular-Accessories',
								eventLabel: sku
							});

						}

					});

				});

				$(widgetSelector).on('lpCollapsible:expand', () => {

					trackProductPageEvents({
						eventAction: 'Open-Popular-Accessories'
					});

				});

			}

		}

		function initializeDataCapture () {

			if (!enableDataCapture) {

				return;

			}

			const { reporter } = window.lp.dataCapture;

			const elementObserver = visibilityObserver((element) => {

				const data = Reporter.getSkuViewData(element);
				reporter.track(data);

			});

			const elementWithAttributeBelowObserver = visibilityObserver((element) => {

				const skuView = element.querySelector('[data-sku]');
				const data = Reporter.getSkuViewData(skuView);
				reporter.track(data);

			});

			const elementWithAttributeAboveObserver = visibilityObserver((element) => {

				const skuView = element.closest('[data-sku]');
				const data = Reporter.getSkuViewData(skuView);
				reporter.track(data);

			});

			const buildFullSystemElements = document.querySelectorAll(
				'[data-capture-section="BuildFullSystem"] img[data-sku]'
			);
			buildFullSystemElements.forEach((element) => elementObserver.observe(element));

			// Skip Gift Card as SkuView events will be bundled with other Gift Card SKUs in giftCard js
			if (!isGiftCard) {

				const pdpMainProductActiveDivElement = document.querySelectorAll(
					'[data-capture-section="PDPMainProduct"] div.pdImgWrap.active'
				);
				pdpMainProductActiveDivElement.forEach((element) => {

					const skuView = element.closest('[data-sku]');
					const data = Reporter.getSkuViewData(skuView);
					reporter.track(data, { immediate: true });

				});

			}

			const pdpInstallationSku = document.getElementById('addInstallationChk');
			if (pdpInstallationSku) {

				elementObserver.observe(pdpInstallationSku);

			}

			const moreOptions = document.querySelectorAll('[data-capture-section="SliderMoreOptions"] img');
			moreOptions.forEach((element) => elementObserver.observe(element));

			const shadeOptions = document.querySelectorAll('[data-capture-section="SliderShadeOptions"] img[data-sku]');
			shadeOptions.forEach((element) => elementObserver.observe(element));

			const baseOptions = document.querySelectorAll('[data-capture-section="SliderBaseOptions"] img[data-sku]');
			baseOptions.forEach((element) => elementObserver.observe(element));

			const downrods = document.querySelectorAll('[data-capture-section="Downrods"] input[data-sku]');
			downrods.forEach((element) => elementObserver.observe(element));

			const controls = document.querySelectorAll('[data-capture-section="Controls"] input[data-sku]');
			controls.forEach((element) => elementObserver.observe(element));

			const popularAccessories = document.querySelectorAll(
				'[data-capture-section="PopularAccessories"] img[data-sku]'
			);
			popularAccessories.forEach((element) => elementObserver.observe(element));

			const multiProdSizeOptions = document.querySelectorAll(
				'[data-capture-section="AvailableOptions"] .multiProdSizeOptions__option'
			);
			const dimmerOptions = document.querySelectorAll('[data-capture-section="DimmerFacePlateOptions"] li');

			multiProdSizeOptions.forEach((element) => elementWithAttributeBelowObserver.observe(element));
			dimmerOptions.forEach((element) => elementWithAttributeBelowObserver.observe(element));

			const allBaseColors = document.querySelectorAll('[data-capture-section="AllBaseColors"] img[data-sku]');
			allBaseColors.forEach((element) => elementObserver.observe(element));

			const customerAlsoViewed = document.querySelectorAll(
				'[data-capture-section="CustomerAlsoViewed"] img[data-sku]'
			);
			customerAlsoViewed.forEach((element) => elementObserver.observe(element));

			const housingOptions = document.querySelectorAll(
				'[data-capture-section="PdpHousingOptions"] img[data-sku]'
			);
			housingOptions.forEach((element) => elementObserver.observe(element));

			if (showRecommendedBulbs) {

				document
					.querySelectorAll('[data-capture-section="RecommendedBulbsPDP"] img[data-sku]')
					.forEach((element) => elementObserver.observe(element));

			}

			document.addEventListener('recommendedItems:colorPlus:relatedItems', () => {

				// for color plus that has data-sku on the DIV container of the image
				const colorPlusRecommendedItemElements = document.querySelectorAll(
					'[data-capture-section="RelatedItems"] div[data-sku] img'
				);
				colorPlusRecommendedItemElements.forEach((element) => elementWithAttributeAboveObserver.observe(element));

			});

			document.addEventListener('recommendedItems:loaded', () => {

				const moreYouMayLikeElements = document.querySelectorAll(
					'[data-capture-section="MoreYouMayLikeWidget"] img'
				);
				const recommendedItemsElements = document.querySelectorAll(
					'[data-capture-section="RelatedItems"] img[data-sku]'
				);
				const similarDesignsViewed = document.querySelectorAll(
					'[data-capture-section="SimilarDesigns"] img[data-sku]'
				);

				similarDesignsViewed.forEach((element) => elementWithAttributeAboveObserver.observe(element));
				moreYouMayLikeElements.forEach((element) => elementWithAttributeAboveObserver.observe(element));
				recommendedItemsElements.forEach((element) => elementWithAttributeAboveObserver.observe(element));

			});

			// Added for server side rendering of Related Items - Logged in as employee with store not in session
			const recommendedItemsSSElements = document.querySelectorAll(
				'[data-capture-section="RelatedItems"] img[data-sku]'
			);
			recommendedItemsSSElements.forEach((element) => elementWithAttributeAboveObserver.observe(element));

		}

		const giftCardInfo = document.querySelector('.pdGiftCardInfo');

		function callNotifyMeApi (userEmail) {

			return fetch('/api/emails/products/itememailnotification/', {
				method: 'post',
				headers: {
					Accept: 'application/json, text/plain, */*',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ ShortSku: window.shortSku, EmailAddress: userEmail })
			});

		}

		function initNotifyMe () {

			const notifyMeForm = document.getElementById('notify-me-form');
			const submitButton = document.getElementById('notifyMeSubmitBtn');

			if (notifyMeForm) {

				trackProductPageEvents({
					eventAction: 'Load-Notify-Me',
					eventLabel: window.shortSku,
					nonInteraction: 1
				});
				setGenericEvent({
					event_name: 'pdp_notifyme_load',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

				initBlockEnterKeyForDisabledButton(submitButton);
				window.initFormValidation(true, '#notify-me-form', {
					errorElement: 'span',
					success: 'valid',
					invalidHandler: function invalidHandler (form, validator) {

						const errors = validator.numberOfInvalids();

						if (errors) {

							validator.errorList[0].element.focus();

						}

					}
				});

				notifyMeForm.addEventListener('submit', (e) => {

					e.preventDefault();

					const genericErrorMessage = 'Sorry! We ran into a problem. Please try again later.';
					if ($(notifyMeForm).valid()) {

						const emailField = document.getElementById('notifyMeEmail');
						const userEmail = emailField ? emailField.value : '';
						const errorMessage = document.getElementById('notifyMeError');
						const successMessage = document.getElementById('notifyMeSuccess');
						successMessage.style.display = 'none';
						errorMessage.style.display = 'none';

						const request = callNotifyMeApi(userEmail)
							.then((response) => {

								if (response.status === 200) {

									successMessage.style.display = 'flex';
									notifyMeForm.style.display = 'none';

									trackProductPageEvents({
										eventAction: 'Submit-Notify-Me',
										eventLabel: window.shortSku,
										nonInteraction: 1
									});
									setGenericEvent({
										event_name: 'pdp_notifyme_submit',
										pdp_item_id: window.shortSku,
										pdp_prime_cat: window.productCategory
									});

								} else {

									errorMessage.innerText = response.ErrorMessage || genericErrorMessage;

									successMessage.style.display = 'none';
									errorMessage.style.display = 'block';

								}

							})
							.catch((error) => {

								errorMessage.innerText = genericErrorMessage;
								successMessage.style.display = 'none';
								errorMessage.style.display = 'block';

							});

						setButtonStateDuringActivity(submitButton, request);

					}

				});

			}

		}

		async function init () {

			if (getQueryStringValue('turntoEmailType')) {

				dynamicImport(
					'desktop/product-injectTurnToSpeedFlex',
					'../../../features/productReviews/injectTurnToSpeedFlex'
				).then((module) => {

					const initTurnToSpeedFlex = module.default;
					initTurnToSpeedFlex();

					dynamicImport(
						'desktop/product-initProductReviewFormTracking',
						'../../../features/productReviews/analytics/initProductReviewFormTracking'
					).then((analyticsModule) => {

						const initProductReviewFormTracking = analyticsModule.default;
						initProductReviewFormTracking();

					});

				});

			}

			const { isInstallationProviderEligible } = window.lp.product;

			if (isInstallationProviderEligible) {

				dynamicImport('desktop/product-installationProvider', './installationProvider').then((module) => {

					const initInstallationProvider = module.default;
					initInstallationProvider();

				});

			}

			const printableLifeSizeImg = document.querySelector('#printableLifeSizeImg');

			if (printableLifeSizeImg) {

				dynamicImport('desktop/product-printableLifeSizeImg', './printableLifeSizeImg').then((module) => {

					const initPrintableLifeSizeImg = module.default;
					initPrintableLifeSizeImg();

				});

				trackProductPageEvents({
					eventCategory: 'PDP',
					eventAction: 'Load-Printable-PDF',
					eventLabel: window.shortSku,
					nonInteraction: 1
				});

			}

			initializeDataCapture();

			// certona
			window.lp.certona.getRecommendedItems();

			try {

				// Energy Info
				const showEnergyInfo = document.querySelector('#jsEnergyInfoLogo');

				if (showEnergyInfo) {

					dynamicImport('desktop/product-energyInfo', './energyInfo').then((module) => {

						const initEnergyInfo = module.default;
						initEnergyInfo();

					});

				}

				// input filters
				initInputFilters();

				// cart
				initCart(isOpenBox, sortPath, sortPosition, sortSku, listType, searchSkuCount).init();

				// gift card
				if (giftCardInfo) {

					initGiftCard().init();

				}

				// wishlist
				initWishList().init();
				// sticky header
				initStickyHeader().init();

				// pd images (full screen, thumbnails, etc)
				// init on page load to allow A/B test to update HTML before Glider is initialized
				window.addEventListener('load', () => initImage().init());

				// chat, social media, print, etc
				initShareProduct().init();

				unveil('img.unveil[data-src]');

				if (isUserBopusEligible) {

					initializeBopus();

				}

				// related videos
				if (window.hasVideos) {

					relatedVideos().init();

				}

				// product assets
				intializeProductAssets();

				initLpSelect();

				const isCeilingFanPdp = window.utag_data.product_category.includes('Ceiling Fans');
				if (isCeilingFanPdp) {

					dynamicImport('desktop/product-ceilingFan', './ceilingFan').then((module) => {

						const initCeilingFan = module.default;
						initCeilingFan();

					});

				}

				widgetPopularTrackingHandler();

				// recommended bulbs
				if (showRecommendedBulbs) {

					dynamicImport('desktop/product-recommendedBulbs', './recommendedBulbs/initRecommendedBulbs').then(
						(module) => {

							const initRecommendedBulbs = module.default;
							initRecommendedBulbs(recommendedBulbTypeCount, sortPath, sortPosition, sortSku, listType);

						}
					);

				}

				const productCompleteTheLook = document.querySelector('#productCompleteTheLookSection');
				if (productCompleteTheLook) {

					dynamicImport('desktop/product-productCompleteTheLook', './productCompleteTheLook/').then(
						(module) => {

							const initProductCompleteTheLook = module.default;
							const { shortSku } = window;
							const { collageId } = window.lp.product;

							initProductCompleteTheLook(productCompleteTheLook, shortSku, collageId);

						}
					);

				}

				const productQuestionsAndAnswersElement = document.querySelector(
					'#productQuestionsAndAnswersContainer'
				);
				const productReviewsElement = document.querySelector('#productReviewsContainer');

				if (productQuestionsAndAnswersElement) {

					initOnIntersection(
						productQuestionsAndAnswersElement,
						() => {

							const request = dynamicImport(
								'desktop/product-productQuestionsAndAnswers',
								'./productQuestionsAndAnswers'
							).then((module) => {

								const initProductQuestionsAndAnswers = module.default;

								initProductQuestionsAndAnswers(pageState, productQuestionsAndAnswersElement);
								document
									.getElementById('turntoQuestionsAndAnswersSection')
									?.classList.remove('notInitialized');

							});

							lazyPageFeatureRequests.push(request);

						},
						'0px 0px 600px 0px'
					);

				}

				if (productReviewsElement) {

					initOnIntersection(
						productReviewsElement,
						() => {

							const request = dynamicImport('desktop/product-productReviews', './productReviews').then(
								(module) => {

									const initProductReviews = module.default;

									initProductReviews(pageState, productReviewsElement);
									document.getElementById('turntoReviewsSection')?.classList.remove('notInitialized');

								}
							);

							lazyPageFeatureRequests.push(request);

						},
						'0px 0px 600px 0px'
					);

				}

				// ATP (Start)
				const atpContainerPdp = document.querySelector('.atpContainerPdp');
				if (atpContainerPdp) {

					// Default ATP Fallback labels
					const fallbackCalloutText = showShipsToday
						? FALLBACK_CALLOUT_TEXT.SHIPS
						: FALLBACK_CALLOUT_TEXT.SHIPS_IN;
					const fallbackCutoffDate = shipsTodayCutoffTime;
					const fallbackDateLabel = atpShippingFallbackLabel;
					const cutoffDateCalloutText = showShipsToday && !shipsTodayIsBeforeCutoff
						? FALLBACK_CALLOUT_TEXT.IF_ORDERED_BY
						: FALLBACK_CALLOUT_TEXT.IF_ORDERED_IN;
					const defaultFallbackVariation = FALLBACK_VARIATIONS.USE_SHIPS_FALLBACK;

					const fallbackCallback = () => {

						atpContainerPdp.classList.remove('skeleton');
						atpContainerPdp.classList.add('hidden');

					};
					const isMultiProduct = atpContainerPdp.classList.contains('atpContainerPdp--multi');

					if (isMultiProduct) {

						let atpDeliveryInfo = null;
						const getShortSkus = () => Array.from(
							document.querySelectorAll('input[name="MultiproductRadio"]')
						).map((el) => ({ shortSku: el.value, isSelected: el.checked }));
						const shortSkus = getShortSkus();

						const onZipChangeSuccess = async (zipCode) => {

							atpContainerPdp.classList.add('skeleton');

							try {

								const newAtpDeliveryInfo = await getMultipleDeliveryInfoAndRender({
									products: getShortSkus(), // Get shortSkus again as selected item might have changed
									zipCode,
									rootElement: atpContainerPdp,
									onZipChangeSuccess,
									fallbackDateLabel,
									fallbackCalloutText,
									cutoffDateCalloutText,
									isInit: false
								});

								pageState.atpDeliveryInfo = newAtpDeliveryInfo;
								pageState.currentShowShipsInVerbiage = atpDeliveryInfo?.showShipsInVerbiage || false;
								pageState.currentIsInternational = atpDeliveryInfo?.isInternational || false;
								pageState.zipCode = zipCode;

							} catch (error) {

								// eslint-disable-next-line no-console
								console.error(error);

								const { data } = error;
								const showShipsInVerbiage = data?.showShipsInVerbiage || false;
								const isInternational = data?.isInternational || false;

								pageState.atpDeliveryInfo = null;
								pageState.zipCode = zipCode;
								pageState.currentShowShipsInVerbiage = showShipsInVerbiage;
								pageState.currentIsInternational = isInternational;

								if (fallbackDateLabel) {

									const deliveryDate = '';
									const deliveryDateLabel = '';
									const cutoffDate = fallbackCutoffDate;

									renderAtpDeliveryInfo({
										rootElement: atpContainerPdp,
										deliveryDate,
										deliveryDateLabel,
										fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
										fallbackCalloutText,
										showShipsInVerbiage,
										cutoffDate,
										cutoffDateCalloutText,
										zipCode,
										onZipChangeSuccess
									});

									return;

								}

								fallbackCallback();

							}

						};

						atpDeliveryInfo = await initMultipleAtpDeliveryInfo({
							rootElement: atpContainerPdp,
							products: shortSkus,
							isAtpToggleOn,
							fallbackCallback,
							onZipChangeSuccess,
							fallbackDateLabel,
							fallbackCalloutText,
							cutoffDate: fallbackCutoffDate,
							cutoffDateCalloutText
						});

						pageState.atpDeliveryInfo = atpDeliveryInfo?.deliveryInfo;
						pageState.zipCode = atpDeliveryInfo?.zipCode;
						pageState.onZipChangeSuccess = onZipChangeSuccess;
						pageState.fallbackCallback = fallbackCallback;
						pageState.fallbackCalloutText = fallbackCalloutText;
						pageState.shipsTodayFallbackLabel = fallbackDateLabel;
						pageState.showShipsToday = showShipsToday;
						pageState.cutoffDateCalloutText = cutoffDateCalloutText;
						pageState.defaultFallbackVariation = defaultFallbackVariation;
						pageState.currentShowShipsInVerbiage = atpDeliveryInfo?.showShipsInVerbiage || false;
						pageState.currentIsInternational = atpDeliveryInfo?.isInternational || false;

					} else if (kitSkuComponents?.length > 0) {

						const onZipChangeSuccess = async (zipCode) => {

							atpContainerPdp.classList.add('skeleton');

							let deliveryDate = '';
							let deliveryDateLabel = '';
							let cutoffDate = fallbackCutoffDate;
							let showShipsInVerbiage = false;
							let isInternational = false;

							const deliveryDateResponse = isAtpToggleOn
								? await getProductDeliveryDates(kitSkuComponents, true)
								: null;

							if (deliveryDateResponse) {

								const models = deliveryDateResponse.ResponseModel || [];
								if (deliveryDateResponse.IsSuccess) {

									const latestDeliveryInfo = getLatestDeliveryInfoForKitSkus(
										models,
										kitSkuComponents
									);

									if (latestDeliveryInfo) {

										deliveryDate = latestDeliveryInfo.DeliveryDate;
										deliveryDateLabel = latestDeliveryInfo.DeliveryDateLabel;
										cutoffDate = latestDeliveryInfo.CutoffTime;
										showShipsInVerbiage = latestDeliveryInfo.ShowShipsInVerbiage;
										isInternational = latestDeliveryInfo.IsInternational;

									}

								} else if (models.length > 0) {

									// flags will always be the same for all KitSKUs
									showShipsInVerbiage = models[0].ShowShipsInVerbiage;
									isInternational = models[0].IsInternational;

								}

							}

							renderAtpDeliveryInfo({
								rootElement: atpContainerPdp,
								deliveryDate,
								deliveryDateLabel,
								fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
								fallbackCalloutText,
								showShipsInVerbiage,
								cutoffDate,
								cutoffDateCalloutText,
								zipCode,
								onZipChangeSuccess
							});

						};

						initAtpDeliveryInfoForKitSkus({
							rootElement: atpContainerPdp,
							kitSkuComponents,
							isAtpToggleOn,
							fallbackCallback,
							onZipChangeSuccess,
							fallbackDateLabel,
							fallbackCalloutText,
							cutoffDate: fallbackCutoffDate,
							cutoffDateCalloutText
						});

					} else {

						const onZipChangeSuccess = async (zipCode) => {

							atpContainerPdp.classList.add('skeleton');

							let deliveryDate = '';
							let deliveryDateLabel = '';
							let cutoffDate = fallbackCutoffDate;

							const deliveryDateResponse = isAtpToggleOn
								? await getProductDeliveryDate(window.shortSku, isOpenBox, true)
								: null;

							const model = deliveryDateResponse?.ResponseModel;
							if (deliveryDateResponse?.IsSuccess) {

								deliveryDate = model.DeliveryDate;
								deliveryDateLabel = model.DeliveryDateLabel;
								cutoffDate = model.CutoffTime;

							}

							const isInternational = model?.IsInternational || false;
							const showShipsInVerbiage = model?.ShowShipsInVerbiage || false;

							renderAtpDeliveryInfo({
								rootElement: atpContainerPdp,
								deliveryDate,
								deliveryDateLabel,
								fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
								fallbackCalloutText,
								showShipsInVerbiage,
								cutoffDate,
								cutoffDateCalloutText,
								zipCode,
								onZipChangeSuccess
							});

						};

						initAtpDeliveryInfo({
							rootElement: atpContainerPdp,
							shortSku: window.shortSku,
							isAtpToggleOn,
							fallbackCallback,
							onZipChangeSuccess,
							fallbackDateLabel,
							fallbackCalloutText,
							cutoffDate: fallbackCutoffDate,
							cutoffDateCalloutText,
							isOpenBox
						});

					}

				}
				// ATP (End)

				// common
				initCommon(
					pageState,
					employeeNumber,
					inStockLabelDays,
					shippingTerms,
					showPaypalLaterWidget,
					googleMapsApiKey,
					googleMapsApiVersion
				).init();

			} catch (e) {

				// eslint-disable-next-line no-console
				console.error(e);

			}

			if (showPaypalLaterWidget) {

				let paymentPlan;

				renderPayPalMessage({
					amount: window.productPrice,
					selector: '#paypalLaterWidget',
					onRender: (e) => {

						paymentPlan = getPayPalLaterPaymentPlan(e.meta?.creditProductIdentifier);

					},
					onClick: () => {

						trackProductPageEvents({
							eventAction: 'Click-PayPal-Later-Popup',
							eventLabel: `${paymentPlan} for ${formatCurrency(window.productPrice, false, false)}`
						});

					},
					onApply: () => {

						trackProductPageEvents({
							eventAction: 'Click-PayPal-Later-Apply',
							eventLabel: `${paymentPlan} for ${formatCurrency(window.productPrice, false, false)}`
						});

					}
				});

			}

			// track aggregate ratings
			if (ugcApiAvailable) {

				const averageRating = reviewsSummaryData?.averageRating ?? 0;
				const averageRatingFormatted = averageRating > 0 ? averageRating.toFixed(1) : averageRating;
				const numOfReviews = reviewsSummaryData?.reviews ?? 0;

				trackProductPageEvents({
					eventAction: `${averageRatingFormatted} Stars:${numOfReviews} Reviews`,
					eventLabel: window.shortSku,
					nonInteraction: 1
				});

			}

			declareJavaScriptInitialized('page');
			initNotifyMe();

			initFreeShippingTooltip();

		}

		return Object.freeze({
			init
		});

	};

	// initialize page
	lp.pdp.page().init();

	const { globals } = window.lp;
	const mmcIndex = (window.location.search && window.location.search.search('cm_mmc')) || -1;
	if (!globals.isHospitality && productAvailability && productAvailability.callForAvailability && mmcIndex >= 0) {

		const pdRecommendedBulbSection = document.getElementById('pdRecommendedBulbSection');
		const pdBottomContainer = document.querySelector('.pdBottomContainer');
		const productDetailsSection = document.getElementById('productDetailsSection');
		const pdMoreYouMayLike = document.getElementById('pdMoreYouMayLike');

		if (pdRecommendedBulbSection && pdMoreYouMayLike) {

			pdBottomContainer.insertBefore(pdMoreYouMayLike, pdRecommendedBulbSection);
			pdBottomContainer.insertBefore(productDetailsSection, pdRecommendedBulbSection.nextElementSibling);

		} else if (pdMoreYouMayLike) {

			pdBottomContainer.insertBefore(pdMoreYouMayLike, productDetailsSection);

		}

	}

	window.addEventListener('load', async () => {

		// certona events need to be loaded before calling GA
		moreYouMayLikeGAEvents();
		similarDesignsGAEvents();
		relatedItemsGAEvents();

		const atpContainerPdp = document.querySelector('.atpContainerPdp');
		if (atpContainerPdp) {

			let itemShipTime = '';

			try {

				itemShipTime = await waitForElementText(atpContainerPdp);

			} catch (err) {

				itemShipTime = 'ATP timeout error';

			}

			window.utag_data.item_ship_time = [ itemShipTime ];

		}

		viewItem(window.utag_data);

		if (document.querySelector('[data-hero-video-thumbnail]')) {

			// fire GA4 video_load event
			setVideoLoad({
				pageType: window.utag_data.page_type,
				section: 'hero images'
			});

		}

		if (document.querySelector('[data-related-videos]')) {

			// fire GA4 video_load event
			setVideoLoad({
				pageType: window.utag_data.page_type,
				section: 'related videos'
			});

		}

	});

	const trackProductAvailability = () => {

		// track product availability
		if (productAvailability) {

			const { callForAvailability, unavailable, isDiscontinued } = productAvailability;

			if (callForAvailability) {

				utagLinkPDP()({
					eventAction: 'Product Unavailable - Call For Availability',
					eventLabel: window.shortSku,
					nonInteraction: 1
				});

			}
			if (unavailable) {

				if (isDiscontinued) {

					utagLinkPDP()({
						eventAction: 'Product Unavailable - Discontinued',
						eventLabel: window.shortSku,
						nonInteraction: 1
					});

				} else {

					utagLinkPDP()({
						eventAction: 'Product Unavailable - Not Available on PDP',
						eventLabel: window.shortSku,
						nonInteraction: 1
					});

				}

			}

			// track Build Full System load
			const buildFullSystem = document.getElementById('build-full-system');

			if (buildFullSystem) {

				utagLinkPDP()({
					eventAction: `Load-BFS-${window.utag_data.product_category[0]}`,
					eventLabel: window.location.pathname,
					nonInteraction: 1
				});

			}

		}

	};

	const trackSoldOut = () => {

		const pdSoldOut = document.getElementById('soldOutLabel');

		if (pdSoldOut && !pdSoldOut.classList.contains('hidden')) {

			utagLinkPDP()({
				eventAction: 'Product Unavailable - Sold Out',
				eventLabel: window.shortSku,
				nonInteraction: 1
			});

		}
		const multiOption = document.querySelectorAll('.lpSelectWrapper .multiProdSizeOptions >li');
		if (multiOption && multiOption.length > 1) {

			utagLinkPDP()({
				eventAction: 'Load-Multi-Option',
				eventLabel: window.shortSku,
				nonInteraction: 1
			});
			setGenericEvent({
				event_name: 'pdp_multioption_load',
				pdp_item_id: window.shortSku,
				pdp_prime_cat: window.productCategory
			});
			const listName = document.querySelectorAll(
				'.lpSelectWrapper .multiProdSizeOptions >li .multiProdSizeOptions__option-text'
			);
			listName.forEach((ele) => {

				ele.addEventListener('click', (el) => {

					const eventLabel = el.currentTarget.querySelector('.listNameText')
						? el.currentTarget.querySelector('.listNameText').innerText.trim()
						: '';
					utagLinkPDP()({
						eventAction: 'Select-Multi-Option',
						eventLabel: `${initialShortSku}-${eventLabel}`
					});
					setGenericEvent({
						event_name: 'pdp_multioption_select',
						pdp_item_id: window.shortSku,
						pdp_prime_cat: window.productCategory,
						selected_item_id: initialShortSku
					});

				});

			});

		}

	};

	function buyItNewGAEvents () {

		// track buy it new link
		const buyItNewLink = document.querySelector('.openBoxBuyItNewContainer');

		if (buyItNewLink) {

			trackProductPageEvents({
				eventAction: 'Load-Buy It New',
				eventLabel: window.shortSku,
				nonInteraction: 1
			});

			buyItNewLink.addEventListener('click', () => {

				trackProductPageEvents({
					eventAction: 'Click-Buy It New',
					eventLabel: window.shortSku
				});

			});

		}

	}

	function moreYouMayLikeGAEvents () {

		const moreYouMayLikeItem = document.querySelectorAll('#moreYouMayLikeContainer .moreYouMayLikeItem');

		moreYouMayLikeItem.forEach((link, index) => {

			link.addEventListener('click', (e) => {

				const moreYouLikeEl = e.target.closest('.moreYouLikeLink');
				const itemIndex = moreYouLikeEl.getAttribute('data-item-index');
				const dataSku = moreYouLikeEl.querySelector('img')?.getAttribute('data-sku');
				const eventLabel = `${window.shortSku}:${index + 1}:${dataSku}`;

				trackProductPageEvents({
					eventAction: 'Click-More-You-May-Like',
					eventLabel
				});
				setGenericEvent({
					event_name: 'pdp_moreyoumaylike_click',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory,
					selected_item_id: dataSku,
					item_position_number: index + 1
				});

			});

		});

		const forwardButton = document.querySelector('#moreYouMayLikeContainer .slick-next');

		if (forwardButton) {

			forwardButton.addEventListener('click', () => {

				setGenericEvent({ event_name: 'pdp_moreyoumaylike_forward', pdp_item_id: window.shortSku });

			});

		}

		const backButton = document.querySelector('#moreYouMayLikeContainer .slick-prev');

		if (backButton) {

			backButton.addEventListener('click', () => {

				setGenericEvent({ event_name: 'pdp_moreyoumaklike_back', pdp_item_id: window.shortSku });

			});

		}

	}

	function moreLikeViewAllGAEvents () {

		const viewAll = document.querySelector('.moreViewButton > a');
		if (viewAll) {

			viewAll.addEventListener('click', () => {

				trackProductPageEvents({
					eventAction: 'More-You-May-Like-ViewAll',
					eventLabel: window.shortSku
				});

				setGenericEvent({
					event_name: 'pdp_moreyoumaylike_viewall',
					pdp_item_id: window.shortSku
				});

			});

		}

	}

	function similarDesignsGAEvents () {

		const container = document.getElementById('similarDesigns');

		if (!container || container.classList.contains('hidden')) {

			return;

		}

		const links = container.querySelectorAll('.moreYouMayLikeItem a');
		const viewAllCategoryLink = document.querySelector('.breadcrumbNav__viewAllCategory');
		const expandCollapseButton = container.querySelector('.collapsibleShowHide');

		trackProductPageEvents({
			eventAction: 'Load-Similar-Designs',
			eventLabel: window.shortSku
		});

		links.forEach((link, i) => {

			link.addEventListener('click', () => {

				const eventLabel = `${window.shortSku}:${i + 1}:${link.dataset.certonasku}`;

				trackProductPageEvents({ eventAction: 'Click-Similar-Designs', eventLabel });

			});

		});

		viewAllCategoryLink.addEventListener('click', () => {

			trackProductPageEvents({
				eventAction: `Click-Similar-Designs-View-All-${window.productCategory}`,
				eventLabel: window.shortSku
			});

		});

		expandCollapseButton.addEventListener('click', () => {

			const openOrClose = container.classList.contains('collapsed') ? 'Close' : 'Open';

			trackProductPageEvents({
				eventAction: `${openOrClose}-Similar-Designs`,
				eventLabel: window.shortSku
			});

		});

	}

	function relatedItemsGAEvents () {

		const pdRelatedItems = document.querySelectorAll('.pdRelatedItems, .pdRelItmsProd');
		const container = document.getElementById('pdRelItmsContainer');

		pdRelatedItems.forEach((link, index) => {

			link.addEventListener('click', (e) => {

				const reletedItems = e.target.closest('.pdRelItmsProd');
				const dataSku = reletedItems?.querySelector('img')?.getAttribute('data-sku');
				const eventLabel = `${window.shortSku}:${index + 1}:${dataSku}`;
				const certonaTitle = document.querySelector('.certonaTitle').innerHTML;
				const eventAction = `Click-${certonaTitle.replace(/ /g, '-')}`;

				trackProductPageEvents({
					eventAction,
					eventLabel
				});

				setGenericEvent({
					event_name: 'pdp_relateditems_click',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory,
					selected_item_id: dataSku,
					item_position_number: index + 1
				});

			});

		});

		if (container) {

			container.addEventListener('click', (e) => {

				const { target } = e;
				const pdRelItmsProdShadeOptions = document.querySelectorAll('.pdRelItmsProdShadeOptions');
				const closestItem = target.closest('.pdRelItmsProdShadeOptions');

				// Art shade related items. Clicked anchor or any children of anchor
				if (closestItem && closestItem.querySelector('a').contains(target)) {

					const position = [ ...pdRelItmsProdShadeOptions ].findIndex((elem) => elem.contains(target)) + 1;
					const { sku } = closestItem.dataset;
					const eventLabel = `${window.shortSku}:${position}:${sku}`;

					trackProductPageEvents({
						eventAction: 'Click-Art-Shade-Related-Item',
						eventLabel
					});
					setGenericEvent({
						event_name: 'pdp_artshade_related_click',
						pdp_item_id: window.shortSku,
						pdp_prime_cat: window.productCategory,
						selected_item_id: sku
					});

				}

			});

		}

	}

	function allBaseColorsGAEvents () {

		const allBaseColorsLinks = document.querySelectorAll('#all-base-colors a');

		if (!allBaseColorsLinks.length) {

			return;

		}

		allBaseColorsLinks.forEach((link, i) => {

			link.addEventListener('click', () => {

				const { sku } = link.querySelector('img').dataset;

				trackProductPageEvents({
					eventAction: 'Click-Base-Color',
					eventLabel: `${window.shortSku}:${i + 1}:${sku}`
				});
				setGenericEvent({
					event_name: 'pdp_colorplus_viewall_select',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory,
					selected_item_id: sku,
					item_position_number: i + 1
				});

			});

		});

	}

	function initTurntoTeaserClick () {

		const turntoTeaser = document.querySelectorAll('.pdSummaryTeaser--link');
		turntoTeaser.forEach((element) => {

			element.addEventListener('click', (e) => {

				e.preventDefault();
				utagLinkPDP('TurnTo')({
					eventAction: 'PDP-teaserClick',
					eventLabel: window.shortSku
				});

				initTabHandling('#turntoReviewsSection');
				scrollToSection(e.currentTarget.hash);

			});

		});

	}

	function shippingGAEvents () {

		const pdLowPriceGuarantee = document.querySelector('#pdLowPriceGuarantee');
		// low price guarantee modal
		if (pdLowPriceGuarantee) {

			trackProductPageEvents({
				eventAction: 'Load-LowPriceGuarantee',
				eventLabel: `${window.shortSku}`,
				nonInteraction: 1
			});
			setGenericEvent({
				event_name: 'pdp_pricepolicy_load',
				pdp_item_id: window.shortSku,
				pdp_prime_cat: window.productCategory
			});

			pdLowPriceGuarantee.addEventListener('click', (e) => {

				e.preventDefault();
				$(document.body).lpModal({
					onInit: true,
					lpModalClass: 'lpModal--alt1 lpModal--noScroll',
					width: 470,
					height: 140,
					content: $('#pdLowPriceGuaranteeContent')
				});

				trackProductPageEvents({
					eventAction: 'Click-LowPriceGuarantee',
					eventLabel: `${window.shortSku}`
				});
				setGenericEvent({
					event_name: 'pdp_pricepolicy_click',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			});

		}

	}

	function buyItNewGA4Events () {

		// track buy it new link
		const buyItNewLink = document.querySelector('.openBoxBuyItNewContainer');

		if (buyItNewLink) {

			// loading sequence issue adding 100 milisec timer
			setTimeout(() => {

				setGenericEvent({
					event_name: 'pdp_buyitnew_load',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			}, 100);

			buyItNewLink.addEventListener('click', () => {

				setGenericEvent({
					event_name: 'pdp_buyitnew_click',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			});

		}

	}

	function openBoxGA4Events () {

		// track open box link
		const openBoxLink = document.getElementById('openBoxPdpLinkWrapper');

		if (openBoxLink) {

			// loading sequence issue adding 100 milisec timer
			setTimeout(() => {

				setGenericEvent({
					event_name: 'pdp_openboxavailable_load',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			}, 100);

			openBoxLink.addEventListener('click', () => {

				setGenericEvent({
					event_name: 'pdp_openboxavailable_click',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			});

		}

	}

	function artShadeLinksGA4Event () {

		const artShadeLinks = document.querySelector('#artShadeLinks');

		if (artShadeLinks) {

			artShadeLinks.addEventListener('click', () => {

				setGenericEvent({
					event_name: 'pdp_artshade_category_viewall',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			});

		}

	}

	function viewAllColorsGA4Event () {

		const viewAllColor = document.querySelector('#prodViewAllColors');

		if (viewAllColor) {

			viewAllColor.addEventListener('click', () => {

				setGenericEvent({
					event_name: 'pdp_colorplus_viewall',
					pdp_item_id: window.shortSku,
					pdp_prime_cat: window.productCategory
				});

			});

		}

	}

	function initDyEngagementTracking () {

		document.addEventListener('recommendedItems:loaded', () => {

			const moreYouMayLikeWidget = document.querySelectorAll('[data-capture-section="MoreYouMayLikeWidget"] a');
			if (moreYouMayLikeWidget.length) {

				trackDyRecommendedItemClickEvent({ selector: moreYouMayLikeWidget });

			}

		});

	}

	function initPromoCountdown () {

		const promoCouponCodeElement = document.querySelector('.promoCouponCode');
		const promoCountdownElement = promoCouponCodeElement?.querySelector('.promoCouponCode__countdown');

		if (promoCountdownElement) {

			renderPromoCountdown(
				promoCountdownElement,
				promoCountdownElement.dataset.endtime,
				promoCountdownElement.dataset.starttime, () => {

					promoCouponCodeElement.remove();

				}
			);

		}

	}

	// base colors elements are present on domready
	allBaseColorsGAEvents();
	initTurntoTeaserClick();
	trackProductAvailability();
	trackSoldOut();
	buyItNewGAEvents();
	moreLikeViewAllGAEvents();
	shippingGAEvents();
	buyItNewGA4Events();
	openBoxGA4Events();
	artShadeLinksGA4Event();
	viewAllColorsGA4Event();
	initCouponModal(couponCode, isCouponCodeValid);
	initDyEngagementTracking();
	prefetchWebpackAssetSet('desktop/cart/overview');
	initPromoCountdown();

});
